import {
  createClient,
  FindOperation,
  getRequestFactory,
  postRequestFactory,
  SaveOperation,
  SearchOperation,
  searchRequestFactory
} from '@infuse/api/common';

import {
  BatchJob,
  BatchTask,
  CancelBatchCommand,
  ConfigurationBatchJob,
  ConfigurationBatchJobCreateRequest,
  FirmwareBatchJob,
  FirmwareBatchJobCreateRequest,
  FirmwareStationTask
} from './model';

const client = createClient('/rest/batch-runner');

export const batchPermissions = {
  CREATE: 'cpms.create-batch-job',
  SEARCH_BATCH_JOBS: 'cpms.search-batch-jobs',
  GET_BATCH_JOB: 'cpms.get-batch-job',
  CANCEL_BATCH_JOB: 'cpms.cancel-batch-job',
  SEARCH_BATCH_TASKS: 'cpms.search-batch-tasks',
  GET_BATCH_TASK: 'cpms.get-batch-task',
  CANCEL_BATCH_TASK: 'cpms.cancel-batch-task',
  SEARCH_FIRMWARE_BATCH_JOBS: 'cpms.search-firmware-batch-jobs',
  GET_FIRMWARE_BATCH_JOB: 'cpms.get-firmware-batch-job',
  SEARCH_FIRMWARE_BATCH_TASKS: 'cpms.search-firmware-batch-tasks',
  GET_FIRMWARE_BATCH_TASK: 'cpms.get-firmware-batch-task'
};

// --- Generic batch job/task endpoints (all types: firmware, configuration, etc.) ---

export const searchBatchJobs: SearchOperation<BatchJob> = searchRequestFactory<BatchJob>(client, {
  pattern: '/batch-job',
  permissionId: batchPermissions.SEARCH_BATCH_JOBS
});

export const getBatchJob: FindOperation<BatchJob, { batchJobId: string }> = getRequestFactory<
  BatchJob,
  { batchJobId: string }
>(client, {
  pattern: '/batch-job/{batchJobId}',
  permissionId: batchPermissions.GET_BATCH_JOB
});

export const searchBatchTasks: SearchOperation<BatchTask> = searchRequestFactory<BatchTask>(
  client,
  {
    pattern: '/batch-task',
    permissionId: batchPermissions.SEARCH_BATCH_TASKS
  }
);

export const getBatchTask: FindOperation<BatchTask, { taskId: string }> = getRequestFactory<
  BatchTask,
  { taskId: string }
>(client, {
  pattern: '/batch-task/{taskId}',
  permissionId: batchPermissions.GET_BATCH_TASK
});

export const cancelBatchJob: SaveOperation<CancelBatchCommand | undefined, void> = postRequestFactory<
  CancelBatchCommand | undefined,
  void
>(client, {
  pattern: '/batch-job/{batchJobId}/cancel',
  permissionId: batchPermissions.CANCEL_BATCH_JOB
});

export const cancelBatchTask: SaveOperation<CancelBatchCommand | undefined, void> = postRequestFactory<
  CancelBatchCommand | undefined,
  void
>(client, {
  pattern: '/batch-task/{taskId}/cancel',
  permissionId: batchPermissions.CANCEL_BATCH_TASK
});

// --- Firmware-specific endpoints ---

export const createFirmwareBatchJob: SaveOperation<FirmwareBatchJobCreateRequest, FirmwareBatchJob> =
  postRequestFactory<FirmwareBatchJobCreateRequest, FirmwareBatchJob>(client, {
    pattern: '/firmware/batch-job',
    permissionId: batchPermissions.CREATE
  });

export const searchFirmwareBatchJobs: SearchOperation<FirmwareBatchJob> = searchRequestFactory<FirmwareBatchJob>(
  client,
  {
    pattern: '/firmware/batch-job',
    permissionId: batchPermissions.SEARCH_FIRMWARE_BATCH_JOBS
  }
);

export const getFirmwareBatchJob: FindOperation<FirmwareBatchJob, { batchJobId: string }> = getRequestFactory<
  FirmwareBatchJob,
  { batchJobId: string }
>(client, {
  pattern: '/firmware/batch-job/{batchJobId}',
  permissionId: batchPermissions.GET_FIRMWARE_BATCH_JOB
});

export const searchFirmwareBatchTasks: SearchOperation<FirmwareStationTask> = searchRequestFactory<FirmwareStationTask>(
  client,
  {
    pattern: '/firmware/batch-task',
    permissionId: batchPermissions.SEARCH_FIRMWARE_BATCH_TASKS
  }
);

export const getFirmwareBatchTask: FindOperation<FirmwareStationTask, { taskId: string }> = getRequestFactory<
  FirmwareStationTask,
  { taskId: string }
>(client, {
  pattern: '/firmware/batch-task/{taskId}',
  permissionId: batchPermissions.GET_FIRMWARE_BATCH_TASK
});

// --- Configuration-specific endpoints ---
// Backend module is not built yet (batch-runner/configuration is a skeleton); Peblar still
// uses the deprecated legacy client in @infuse/api/installer/pool until it lands.

export const createConfigurationBatchJob: SaveOperation<ConfigurationBatchJobCreateRequest, ConfigurationBatchJob> =
  postRequestFactory<ConfigurationBatchJobCreateRequest, ConfigurationBatchJob>(client, {
    pattern: '/configuration/batch-job',
    permissionId: batchPermissions.CREATE
  });
